import type { DashboardStats } from "../api/types";

/**
 * The summary's two drawings: the greeting, and the journey a comment makes until the
 * person answers.
 *
 * The journey is the panel's whole promise in four steps — someone comments, gets an
 * answer, a conversation opens, they write back — and each step is drawn with its own
 * colour and how much of the previous one made it through.
 */

export type Stage = "comment" | "public" | "dm" | "reply";

export interface JourneyStep {
  stage: Stage;
  label: string;
  value: number;
  hint: string;
  /** Share of the previous step that reached this one, as "56%"; empty on the first. */
  rateLabel: string;
  /** 0–100, for the bar under the figure. */
  fill: number;
}

const ratio = (part: number, whole: number) => (whole > 0 ? Math.min(1, part / whole) : 0);

export function toJourneyView(
  stats: Partial<Pick<DashboardStats, "events" | "outcomes" | "conversations" | "replies">>,
): JourneyStep[] {
  const comments = stats.events ?? 0;
  const delivered = stats.outcomes?.delivered ?? 0;
  const conversations = stats.conversations ?? 0;
  const replies = stats.replies ?? 0;

  const steps: Omit<JourneyStep, "rateLabel" | "fill">[] = [
    { stage: "comment", label: "Comentarios", value: comments, hint: "llegaron a tus publicaciones" },
    { stage: "public", label: "Atendidos", value: delivered, hint: "con la palabra clave, recibieron respuesta" },
    { stage: "dm", label: "Conversaciones", value: conversations, hint: "se abrieron por mensaje privado" },
    { stage: "reply", label: "Respuestas", value: replies, hint: "te escribieron de vuelta" },
  ];

  return steps.map((step, index) => {
    const previous = index === 0 ? null : steps[index - 1]!.value;
    const rate = previous === null ? 1 : ratio(step.value, previous);
    return {
      ...step,
      rateLabel: previous === null || previous === 0 ? "" : `${Math.round(rate * 100)}%`,
      fill: step.value > 0 ? Math.max(6, Math.round(rate * 100)) : 0,
    };
  });
}

/** "Buenos días" until noon, "Buenas tardes" until eight, then "Buenas noches". */
export function greeting(now: Date = new Date()): string {
  const hour = now.getHours();
  if (hour >= 5 && hour < 12) return "Buenos días";
  if (hour >= 12 && hour < 20) return "Buenas tardes";
  return "Buenas noches";
}

/** What deserves attention right now, or nothing when all is well. */
export function toAttentionView(stats: Partial<Pick<DashboardStats, "failed" | "pending">>) {
  const failed = stats.failed ?? 0;
  const pending = stats.pending ?? 0;
  if (failed > 0) {
    return {
      tone: "danger" as const,
      title: failed === 1 ? "Un envío necesita tu ayuda" : `${failed} envíos necesitan tu ayuda`,
      text: "No saldrán solos: revísalos en el registro y reinténtalos.",
      link: "/actividad?resultado=failed",
      linkLabel: "Revisar",
    };
  }
  if (pending > 0) {
    return {
      tone: "warning" as const,
      title: pending === 1 ? "Un mensaje en cola" : `${pending} mensajes en cola`,
      text: "Saldrán solos en cuanto les toque, respetando tu ritmo de envío.",
      link: "/actividad?resultado=queued",
      linkLabel: "Ver cola",
    };
  }
  return null;
}
